<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">

        <!-- Toggle Filter Button (top-left corner) -->
        <button @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter"
                class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
                        <select v-model="filters.entity" class="w-full px-2 py-1 border outline-none">
                            <option value="ADPOLY">ADPOLY</option>
                            <option value="FCHS">FCHS</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Campus</label>
                        <div class="border px-4 py-2 h-auto flex items-center relative cursor-pointer"
                            :class="activeDropDown == 'campus' ? 'z-30' : ''">
                            <div class="flex flex-grow justify-between items-center"
                                @click.stop="showCampusDropDown = !showCampusDropDown">
                                <div class="text-sm mr-2 sm:mb-0 flex-shrink-0">All</div>
                                <icon-chevron-down
                                    class="h-4 w-4 transform transition-transform duration-300 ease-in-out"
                                    :class="{ 'rotate-180': showCampusDropDown }" />
                            </div>
                            <div v-show="showCampusDropDown"
                                class="flex flex-col h-40 overflow-y-auto shadow-lg px-2 pt-2 absolute w-full bg-white border left-0 top-10">
                                <div v-for="(item, index) in filteredCampuses" :key="index"
                                    class="flex items-center mb-2">
                                    <input type="checkbox" :value="item.campus_code" :id="`academic-year-${index}`"
                                        v-model="filters.campus_code" class="mr-2" />
                                    <label :for="`academic-year-${index}`" class="text-sm">{{ item.campus_code
                                        }}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Local or Non Local</label>
                        <select v-model="filters.local_or_non_local" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="Local">Local</option>
                            <option value="Non Local">Non Local</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Gender</label>
                        <select v-model="filters.gender" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                        </select>
                    </div>
                </div>

                <!-- Apply Button -->
                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <div class="container mx-auto px-4 sm:px-8 py-6 space-y-8">
            <div class="flex justify-between items-center flex-wrap gap-4">
                <h1 class="text-2xl font-bold text-gray-800 flex items-center">
                    <font-awesome-icon :icon="['fas', 'graduation-cap']" class="text-blue-600 mr-2" />
                    <div class="uppercase">Graduates Forecast ({{ filters.entity }})</div>
                </h1>
                <div class="flex items-center flex-wrap gap-4">
                    <div v-if="timeStamp" class="flex items-center space-x-2 text-sm text-gray-500 mt-2">
                        <font-awesome-icon :icon="['fas', 'clock']" class="text-gray-400 w-4 h-4" />
                        <span class="font-medium text-gray-700">Last Updated:</span>
                        <span>{{ timeStamp }}</span>
                    </div>
                </div>
            </div>

            <!-- Dashboard Cards -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <!-- Total Expected Graduates Card -->
                <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-blue-100 text-sm font-medium">Total Expected Graduates</p>
                            <p class="text-3xl font-bold mt-2">{{ formatNumber(graduationData.totalGraduates) }}</p>
                            <p class="text-blue-100 text-xs mt-1">Academic Year 2024-2025</p>
                        </div>
                        <div class="bg-blue-400 bg-opacity-30 rounded-full p-3">
                            <font-awesome-icon :icon="['fas', 'users']" class="text-2xl" />
                        </div>
                    </div>
                </div>

                <!-- Male Graduates Card -->
                <div class="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-indigo-100 text-sm font-medium">Male Graduates</p>
                            <p class="text-3xl font-bold mt-2">{{ formatNumber(graduationData.genderDistribution.male)
                                }}</p>
                            <p class="text-indigo-100 text-xs mt-1">{{ ((graduationData.genderDistribution.male /
                                graduationData.totalGraduates) * 100).toFixed(1) }}% of total</p>
                        </div>
                        <div class="bg-indigo-400 bg-opacity-30 rounded-full p-3">
                            <font-awesome-icon :icon="['fas', 'male']" class="text-2xl" />
                        </div>
                    </div>
                </div>

                <!-- Female Graduates Card -->
                <div class="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl shadow-lg p-6 text-white">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-pink-100 text-sm font-medium">Female Graduates</p>
                            <p class="text-3xl font-bold mt-2">{{ formatNumber(graduationData.genderDistribution.female)
                                }}</p>
                            <p class="text-pink-100 text-xs mt-1">{{ ((graduationData.genderDistribution.female /
                                graduationData.totalGraduates) * 100).toFixed(1) }}% of total</p>
                        </div>
                        <div class="bg-pink-400 bg-opacity-30 rounded-full p-3">
                            <font-awesome-icon :icon="['fas', 'female']" class="text-2xl" />
                        </div>
                    </div>
                </div>

                <!-- Growth Rate Card -->
                <!-- <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-green-100 text-sm font-medium">Growth Rate</p>
                            <p class="text-3xl font-bold mt-2">+12.5%</p>
                            <p class="text-green-100 text-xs mt-1">vs Previous Year</p>
                        </div>
                        <div class="bg-green-400 bg-opacity-30 rounded-full p-3">
                            <font-awesome-icon :icon="['fas', 'chart-line']" class="text-2xl" />
                        </div>
                    </div>
                </div> -->
            </div>

            <!-- Charts Section -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <!-- Gender Distribution Chart -->
                <GenderDistributionChart :data="genderChartData" />

                <!-- Regional Distribution Chart -->
                <RegionalDistributionChart :data="graduationData.regionalDistribution" />
            </div>

            <!-- Campus Breakdown Cards -->
            <div class="bg-white rounded-xl shadow-lg p-6">
                <h3 class="text-lg font-semibold text-gray-800 mb-6">Campus Breakdown</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div v-for="(region, index) in regionalData" :key="index"
                        class="bg-gradient-to-br rounded-lg p-4 text-white" :class="region.gradientClass">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium opacity-90">{{ region.name }}</p>
                                <p class="text-2xl font-bold mt-1">{{ formatNumber(region.value) }}</p>
                                <p class="text-xs opacity-75 mt-1">{{ ((region.value / graduationData.totalGraduates) *
                                    100).toFixed(1) }}% of total</p>
                            </div>
                            <div class="bg-white bg-opacity-20 rounded-full p-2">
                                <font-awesome-icon :icon="['fas', 'map-marker-alt']" class="text-lg" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Yearly Graduates and Gender Distribution by Campus Charts -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ExpectedGraduatesByYearChart :data="graduationData.yearlyGraduates" />
                <GenderDistributionByCampusChart :data="graduationData.genderByCampus" />
            </div>

            <!-- Program Statistics Cards -->
            <ProgramStatisticsCards :data="graduationData.programByCampus" />

            <!-- Program Distribution by Campus Chart -->
            <ProgramByCampusChart :data="graduationData.programByCampus" />

            <!-- Interactive Hierarchical Graduates Breakdown -->
            <!-- <InteractiveHierarchyChart :data="graduationData.hierarchicalData" /> -->

            <!-- Campus Comparison Across Years -->
            <CampusComparisonChart :data="graduationData.campusComparisonData" />

            <!-- Dynamic Region Statistics -->
            <RegionStatisticsChart v-for="region in graduationData.regionalDistribution" :key="region.campus_code"
                :region-name="region.campus_code" :total-graduates="region.count"
                :female-graduates="getCampusGenderData(region.campus_code, 'female')"
                :male-graduates="getCampusGenderData(region.campus_code, 'male')"
                :program-data="getProgramDataByCampus(region.campus_code)"
                :sponsor-data="getSponsorDataByCampus(region.campus_code)"
                :nationality-data="getNationalityDataByCampus(region.campus_code)" />

        </div>

        <!-- Loading Spinner -->
        <div v-if="isLoading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import IconChevronDown from "../../components/icons/IconChevronDown.vue";

const GRADUATES_FORECAST_QUERY = gql`
  query GraduatesForecast($entity: String, $campus_code: [String], $local_or_non_local: String, $gender: String) {
    graduatesForecast(entity: $entity, campus_code: $campus_code, local_or_non_local: $local_or_non_local, gender: $gender) {
      error
      error_code
      message
      data {
        totalGraduates
        genderDistribution {
          male
          female
        }
        regionalDistribution {
          campus_code
          count
        }
        yearlyGraduates {
          student_level
          count
        }
        genderByCampus {
          campus
          female
          male
          total
        }
        programByCampus {
          campus_code
          total_expected_graduates
          programs {
            program_name
            expected_graduate_count
          }
        }
        sponsorByCampus {
          campus_code
          total_expected_graduates
          sponsors {
            sponsor_name
            expected_graduate_count
          }
        }
        nationalityByCampus {
          campus_code
          total_expected_graduates
          nationalities {
            nationality_name
            expected_graduate_count
          }
        }
        campusComparisonData {
          student_level
          total_expected_graduates
          campuses {
            campus_code
            expected_graduate_count
          }
        }
      }
    }
  }
`;
import GenderDistributionChart from "./charts/GenderDistributionChart.vue";
import RegionalDistributionChart from "./charts/RegionalDistributionChart.vue";
import ExpectedGraduatesByYearChart from "./charts/ExpectedGraduatesByYearChart.vue";
import GenderDistributionByCampusChart from "./charts/GenderDistributionByCampusChart.vue";
import ProgramStatisticsCards from "./charts/ProgramStatisticsCards.vue";
import ProgramByCampusChart from "./charts/ProgramByCampusChart.vue";
// import HierarchicalGraduatesChart from "./charts/HierarchicalGraduatesChart.vue";
import CampusComparisonChart from "./charts/CampusComparisonChart.vue";
// import AbuDhabiProgramChart from "./charts/AbuDhabiProgramChart.vue";
import RegionStatisticsChart from "./charts/RegionStatisticsChart.vue";
// import InteractiveHierarchyChart from "./charts/InteractiveHierarchyChart.vue";
import { formattedDateWithTimeAmPm } from "../../helpers/utils";

export default {
    name: "graduates-forecast-index",
    components: {
        IconChevronDown,
        GenderDistributionChart,
        RegionalDistributionChart,
        ExpectedGraduatesByYearChart,
        GenderDistributionByCampusChart,
        ProgramStatisticsCards,
        ProgramByCampusChart,
        // HierarchicalGraduatesChart,
        CampusComparisonChart,
        // AbuDhabiProgramChart,
        RegionStatisticsChart,
        // InteractiveHierarchyChart,
    },
    data() {
        return {
            isLoading: false,
            showFilter: false,
            filters: {
                entity: "FCHS",
                campus_code: [],
                local_or_non_local: "",
                gender: "",
            },
            activeDropDown: "",
            campusList: [],
            showCampusDropDown: false,
            graduationData: {
                totalGraduates: 0,
                genderDistribution: {
                    male: 0,
                    female: 0
                },
                regionalDistribution: [],
                yearlyGraduates: [],
                genderByCampus: [],
                programByCampus: [],
                sponsorByCampus: [],
                nationalityByCampus: [],
                campusComparisonData: {
                    years: [],
                    campuses: [],
                    data: {
                        abuDhabi: [],
                        alAin: [],
                        ajman: [],
                        alDhafrah: []
                    }
                }
            },
            regionalData: [],
            hierarchicalData: {
                total: 0,
                years: [],
                campusBreakdown: {}
            },
            campusComparisonData: {
                years: [],
                campuses: [],
                data: {
                    "Abu Dhabi": [],
                    "Al Ain": [],
                    "Ajman": [],
                    "AlDhafrah": []
                }
            },
            abuDhabiPrograms: [],
            abuDhabiSponsors: [],
            abuDhabiNationalities: [],
            // Al Ain Region Data
            alAinData: {
                totalGraduates: 0,
                femaleGraduates: 0,
                maleGraduates: 0,
                programs: [],
                sponsors: [],
                nationalities: []
            },
            // Ajman Region Data
            ajmanData: {
                totalGraduates: 0,
                femaleGraduates: 0,
                maleGraduates: 0,
                programs: [],
                sponsors: [],
                nationalities: []
            },
            // Al Dhafra Region Data
            alDhafraData: {
                totalGraduates: 0,
                femaleGraduates: 0,
                maleGraduates: 0,
                programs: [],
                sponsors: [],
                nationalities: []
            }
        };
    },
    mounted() {
        this.getCampusList();
        this.loadChartData();
    },
    watch: {
        showCampusDropDown(newVal) {
            if (newVal) {
                this.activeDropDown = 'campus';
                this.collapseDropDown('campus');
            }
        },
    },
    computed: {
        filteredCampuses() {
            if (this.filters.entity) {
                return this.campusList.filter(
                    (campus) => campus.entity === this.filters.entity
                );
            } else {
                return this.campusList;
            }
        },
        timeStamp() {
            const settings = this.$store.getters["settings/settings"] || [];
            const lastUpdated = settings.find(setting => setting.name === "last_updated");
            if (lastUpdated && lastUpdated.value) {
                return formattedDateWithTimeAmPm(lastUpdated.value);
            }
            return "";
        },
        genderChartData() {
            return {
                male: this.graduationData.genderDistribution.male,
                female: this.graduationData.genderDistribution.female,
                total: this.graduationData.totalGraduates
            };
        }
    },
    methods: {
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        clearFilters() {
            this.toggleFilter();
            this.filters = {
                entity: "FCHS",
                campus_code: [],
                local_or_non_local: "",
                gender: "",
            };
            this.collapseDropDown();
            this.loadChartData();
        },
        applyFilters() {
            this.toggleFilter();
            this.loadChartData();
        },
        async getCampusList() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
            query GetCampusList($student_status: String, $entity: String) {
              getCampusList(student_status: $student_status, entity: $entity) {
                entity
                campus_code
              }
            }
          `,
                    variables: {
                        student_status: "IG",
                        entity: "",
                    },
                    fetchPolicy: "network-only",
                });
                this.campusList = data.getCampusList;
            } catch (error) {
                console.log(error);
            }
        },
        collapseDropDown(dropDownName = '') {
            if (dropDownName) {
                if (dropDownName != 'campus') {
                    this.showCampusDropDown = false;
                }
            } else {
                this.showCampusDropDown = false;
            }
        },
        async loadChartData() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: GRADUATES_FORECAST_QUERY,
                    variables: {
                        entity: this.filters.entity,
                        campus_code: this.filters.campus_code,
                        local_or_non_local: this.filters.local_or_non_local,
                        gender: this.filters.gender,
                    },
                    fetchPolicy: 'network-only'
                });

                if (response.data.graduatesForecast.error) {
                    console.error("GraphQL Error:", response.data.graduatesForecast.message);
                    return;
                }

                const data = response.data.graduatesForecast.data;

                // Update the graduation data with the response
                this.graduationData = {
                    totalGraduates: data.totalGraduates,
                    genderDistribution: data.genderDistribution,
                    regionalDistribution: data.regionalDistribution,
                    yearlyGraduates: data.yearlyGraduates,
                    genderByCampus: data.genderByCampus,
                    programByCampus: data.programByCampus,
                    sponsorByCampus: data.sponsorByCampus,
                    nationalityByCampus: data.nationalityByCampus,
                    campusComparisonData: data.campusComparisonData,
                };

                // Update regional data for the cards
                this.regionalData = data.regionalDistribution.map((region, index) => {
                    const gradientClasses = [
                        "from-blue-500 to-blue-600",
                        "from-green-500 to-green-600",
                        "from-purple-500 to-purple-600",
                        "from-orange-500 to-orange-600",
                        "from-red-500 to-red-600",
                        "from-yellow-500 to-yellow-600"
                    ];
                    return {
                        name: region.campus_code,
                        value: region.count,
                        gradientClass: gradientClasses[index % gradientClasses.length]
                    };
                });

            } catch (error) {
                console.error("Failed to load graduates forecast data:", error);
                // Show user-friendly error message
                this.$swal({
                    title: 'Error',
                    text: 'Failed to load graduates forecast data. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
            this.isLoading = false;
        },
        formatNumber(num) {
            return num.toLocaleString();
        },
        getCampusGenderData(campusName, genderType) {
            const campusData = this.graduationData.genderByCampus.find(c => c.campus === campusName);
            if (campusData && campusData[genderType] !== undefined) {
                return campusData[genderType];
            }
            return 0;
        },
        getRegionalDistributionCount(campusCode) {
            const campusData = this.graduationData.regionalDistribution.find(c => c.campus_code === campusCode);
            return campusData ? campusData.count : 0;
        },
        getProgramDataByCampus(campusCode) {
            // Get program data from graduationData.programByCampus
            const campusData = this.graduationData.programByCampus.find(campus => campus.campus_code === campusCode);
            if (campusData && campusData.programs) {
                return campusData.programs
                    .map(program => ({
                        name: program.program_name,
                        count: program.expected_graduate_count,
                        color: this.getProgramColor(program.program_name)
                    }))
                    .sort((a, b) => b.count - a.count); // Sort by count in descending order
            }
            return [];
        },
        getSponsorDataByCampus(campusCode) {
            // Get sponsor data from graduationData.sponsorByCampus
            const campusData = this.graduationData.sponsorByCampus.find(campus => campus.campus_code === campusCode);
            if (campusData && campusData.sponsors) {
                return campusData.sponsors
                    .map(sponsor => ({
                        name: sponsor.sponsor_name,
                        count: sponsor.expected_graduate_count,
                        color: this.getSponsorColor(sponsor.sponsor_name)
                    }))
                    .sort((a, b) => b.count - a.count); // Sort by count in descending order
            }
            return [];
        },
        getNationalityDataByCampus(campusCode) {
            // Get nationality data from graduationData.nationalityByCampus
            const campusData = this.graduationData.nationalityByCampus.find(campus => campus.campus_code === campusCode);
            if (campusData && campusData.nationalities) {
                return campusData.nationalities
                    .map(nationality => ({
                        name: nationality.nationality_name,
                        count: nationality.expected_graduate_count,
                        color: this.getNationalityColor(nationality.nationality_name)
                    }))
                    .sort((a, b) => b.count - a.count); // Sort by count in descending order
            }
            return [];
        },
        getProgramColor(programName) {
            // Predefined color palette for consistent colors
            const colorPalette = [
                '#3b82f6', // blue
                '#10b981', // emerald
                '#dc2626', // red
                '#f59e0b', // amber
                '#8b5cf6', // violet
                '#06b6d4', // cyan
                '#ec4899', // pink
                '#84cc16', // lime
                '#f97316', // orange
                '#6366f1', // indigo
                '#14b8a6', // teal
                '#a855f7', // purple
                '#ef4444', // red-500
                '#22c55e', // green-500
                '#eab308', // yellow-500
                '#06b6d4', // cyan-500
                '#8b5cf6', // violet-500
                '#f43f5e', // rose-500
                '#0ea5e9', // sky-500
                '#10b981'  // emerald-500
            ];

            // Create a simple hash function to generate consistent index for each program name
            let hash = 0;
            for (let i = 0; i < programName.length; i++) {
                const char = programName.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32-bit integer
            }

            // Use absolute value and modulo to get index within color palette range
            const index = Math.abs(hash) % colorPalette.length;
            return colorPalette[index];
        },
        getNationalityColor(nationalityName) {
            // Predefined color palette for consistent colors
            const colorPalette = [
                '#3b82f6', // blue
                '#10b981', // emerald
                '#dc2626', // red
                '#f59e0b', // amber
                '#8b5cf6', // violet
                '#06b6d4', // cyan
                '#ec4899', // pink
                '#84cc16', // lime
                '#f97316', // orange
                '#6366f1', // indigo
                '#14b8a6', // teal
                '#a855f7', // purple
                '#ef4444', // red-500
                '#22c55e', // green-500
                '#eab308', // yellow-500
                '#06b6d4', // cyan-500
                '#8b5cf6', // violet-500
                '#f43f5e', // rose-500
                '#0ea5e9', // sky-500
                '#10b981'  // emerald-500
            ];

            // Create a simple hash function to generate consistent index for each nationality name
            let hash = 0;
            for (let i = 0; i < nationalityName.length; i++) {
                const char = nationalityName.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32-bit integer
            }

            // Use absolute value and modulo to get index within color palette range
            const index = Math.abs(hash) % colorPalette.length;
            return colorPalette[index];
        },
        getSponsorColor(sponsorName) {
            // Predefined color palette for consistent colors
            const colorPalette = [
                '#3b82f6', // blue
                '#10b981', // emerald
                '#dc2626', // red
                '#f59e0b', // amber
                '#8b5cf6', // violet
                '#06b6d4', // cyan
                '#ec4899', // pink
                '#84cc16', // lime
                '#f97316', // orange
                '#6366f1', // indigo
                '#14b8a6', // teal
                '#a855f7', // purple
                '#ef4444', // red-500
                '#22c55e', // green-500
                '#eab308', // yellow-500
                '#06b6d4', // cyan-500
                '#8b5cf6', // violet-500
                '#f43f5e', // rose-500
                '#0ea5e9', // sky-500
                '#10b981'  // emerald-500
            ];

            // Create a simple hash function to generate consistent index for each sponsor name
            let hash = 0;
            for (let i = 0; i < sponsorName.length; i++) {
                const char = sponsorName.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32-bit integer
            }

            // Use absolute value and modulo to get index within color palette range
            const index = Math.abs(hash) % colorPalette.length;
            return colorPalette[index];
        }
    },
};
</script>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}
</style>